/**
 * BoardBottomBar — Floating bottom toolbar + zoom controls.
 * Matches the tldraw whiteboard layout from the reference screenshot.
 */

import React, { useRef, useState } from 'react'
import { useBoard, type BoardStyle } from './useBoard'

export type Tool = 'select' | 'pan' | 'draw' | 'eraser' | 'arrow' | 'text' | 'shape' | 'image' | 'frame' | 'line' | 'style'

export type ShapeSubType = 'rect' | 'ellipse' | 'diamond' | 'triangle' | 'star'

interface Props {
  activeTool: Tool
  onToolChange: (tool: Tool) => void
  shapeSubType: ShapeSubType
  onShapeSubTypeChange: (sub: ShapeSubType) => void
}

// ── SVG Icons (simple, no external deps) ────────────────────────────────────

const shapeSubIcons: Record<ShapeSubType, React.ReactNode> = {
  rect: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
    </svg>
  ),
  ellipse: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="12" rx="10" ry="7" />
    </svg>
  ),
  diamond: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2, 22 12, 12 22, 2 12" />
    </svg>
  ),
  triangle: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 3, 22 21, 2 21" />
    </svg>
  ),
  star: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2, 15.09 8.26, 22 9.27, 17 14.14, 18.18 21.02, 12 17.77, 5.82 21.02, 7 14.14, 2 9.27, 8.91 8.26" />
    </svg>
  ),
}

const SHAPE_SUB_LABELS: Record<ShapeSubType, string> = {
  rect: 'Rechteck',
  ellipse: 'Ellipse',
  diamond: 'Raute',
  triangle: 'Dreieck',
  star: 'Stern',
}

const SHAPE_SUB_ORDER: ShapeSubType[] = ['rect', 'ellipse', 'diamond', 'triangle', 'star']

const icons: Partial<Record<Tool, React.ReactNode | null>> = {
  select: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
      <path d="m13 13 6 6" />
    </svg>
  ),
  pan: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" /><path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" />
      <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" /><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 13" />
    </svg>
  ),
  draw: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    </svg>
  ),
  eraser: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" />
      <path d="M22 21H7" /><path d="m5 11 9 9" />
    </svg>
  ),
  arrow: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
    </svg>
  ),
  text: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 7 4 4 20 4 20 7" /><line x1="9" x2="15" y1="20" y2="20" /><line x1="12" x2="12" y1="4" y2="20" />
    </svg>
  ),
  shape: null, // rendered dynamically based on shapeSubType
  image: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </svg>
  ),
  frame: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3h6v0H3zM15 3h6v0h-6zM3 21h6v0H3zM15 21h6v0h-6z" />
      <path d="M3 3v6M21 3v6M3 15v6M21 15v6" />
    </svg>
  ),
  line: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="19" x2="19" y2="5" />
    </svg>
  ),
  style: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20a7.966 7.966 0 0 1-5.002-1.756l.002.001v-.683c0-1.794 1.492-3.25 3.333-3.25h3.334c1.84 0 3.333 1.456 3.333 3.25v.683A7.966 7.966 0 0 1 12 20Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
}

const TOOLS: { tool: Tool; label: string }[] = [
  { tool: 'select', label: 'Auswahl' },
  { tool: 'pan', label: 'Verschieben' },
  { tool: 'draw', label: 'Zeichnen' },
  { tool: 'eraser', label: 'Radierer' },
  { tool: 'arrow', label: 'Pfeil' },
  { tool: 'text', label: 'Text' },
  { tool: 'shape', label: 'Form' },
  { tool: 'image', label: 'Bild' },
  { tool: 'frame', label: 'Rahmen' },
  { tool: 'line', label: 'Linie' },
]

// ── Style panel constants ─────────────────────────────────────────────────────

const STYLE_COLORS = ['#333333', '#9ca3af', '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6']
const STROKE_WIDTHS: { label: string; value: number }[] = [
  { label: 'Duenn', value: 1 },
  { label: 'Mittel', value: 2 },
  { label: 'Dick', value: 4 },
]
const FILL_OPTIONS: { label: string; value: string }[] = [
  { label: 'Keine', value: 'none' },
  { label: 'Halbtransparent', value: 'semi' },
  { label: 'Voll', value: 'solid' },
]
const DASH_OPTIONS: { label: string; value: 'solid' | 'dashed' | 'dotted' }[] = [
  { label: 'Durchgezogen', value: 'solid' },
  { label: 'Gestrichelt', value: 'dashed' },
  { label: 'Gepunktet', value: 'dotted' },
]
const FONT_SIZES: { label: string; value: number }[] = [
  { label: 'Klein', value: 14 },
  { label: 'Mittel', value: 18 },
  { label: 'Gross', value: 24 },
]

export function BoardBottomBar({ activeTool, onToolChange, shapeSubType, onShapeSubTypeChange }: Props) {
  const viewport = useBoard((s) => s.viewport)
  const setViewport = useBoard((s) => s.setViewport)
  const shapes = useBoard((s) => s.shapes)
  const currentStyle = useBoard((s) => s.currentStyle)
  const setCurrentStyle = useBoard((s) => s.setCurrentStyle)
  const [shapeDropdownOpen, setShapeDropdownOpen] = useState(false)
  const [stylePanelOpen, setStylePanelOpen] = useState(false)
  const shapeDropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const zoomPct = Math.round(viewport.zoom * 100)

  const zoomIn = () => setViewport({ zoom: Math.min(5, viewport.zoom * 1.25) })
  const zoomOut = () => setViewport({ zoom: Math.max(0.1, viewport.zoom / 1.25) })

  // Fit all shapes into view
  const zoomFit = () => {
    const arr = Array.from(shapes.values())
    if (arr.length === 0) {
      setViewport({ x: 0, y: 0, zoom: 1 })
      return
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const s of arr) {
      if (s.x < minX) minX = s.x
      if (s.y < minY) minY = s.y
      if (s.x + s.w > maxX) maxX = s.x + s.w
      if (s.y + s.h > maxY) maxY = s.y + s.h
    }
    const pad = 80
    const cw = window.innerWidth - 60 // sidebar width
    const ch = window.innerHeight - 44 // topbar height
    const contentW = maxX - minX + pad * 2
    const contentH = maxY - minY + pad * 2
    const zoom = Math.min(1, Math.min(cw / contentW, ch / contentH))
    const cx = (cw / 2) - ((minX + maxX) / 2) * zoom
    const cy = (ch / 2) - ((minY + maxY) / 2) * zoom
    setViewport({ x: cx, y: cy, zoom })
  }

  // Shape tool: click cycles, right-click / long-press opens dropdown
  const handleShapeClick = () => {
    if (activeTool === 'shape') {
      // Cycle to next sub-type
      const idx = SHAPE_SUB_ORDER.indexOf(shapeSubType)
      const next = SHAPE_SUB_ORDER[(idx + 1) % SHAPE_SUB_ORDER.length]
      onShapeSubTypeChange(next)
    } else {
      onToolChange('shape')
    }
  }

  const handleShapeContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    onToolChange('shape')
    setShapeDropdownOpen((v) => !v)
  }

  const handleShapePointerDown = () => {
    shapeDropdownTimer.current = setTimeout(() => {
      setShapeDropdownOpen(true)
    }, 500)
  }

  const handleShapePointerUp = () => {
    if (shapeDropdownTimer.current) {
      clearTimeout(shapeDropdownTimer.current)
      shapeDropdownTimer.current = null
    }
  }

  return (
    <>
      {/* ── Zoom controls (bottom-left) ─────────────────────────── */}
      <div style={{
        position: 'absolute', bottom: 12, left: 12, zIndex: 100,
        display: 'flex', alignItems: 'center', gap: 2,
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
        padding: '2px 4px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      }}>
        <button onClick={zoomOut} style={zoomBtnStyle} title="Verkleinern">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12" /></svg>
        </button>
        <span style={{ fontSize: 11, color: '#666', minWidth: 32, textAlign: 'center', userSelect: 'none' }}>
          {zoomPct}%
        </span>
        <button onClick={zoomIn} style={zoomBtnStyle} title="Vergrößern">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
        </button>
        <div style={{ width: 1, height: 16, background: '#e5e7eb', margin: '0 2px' }} />
        <button onClick={zoomFit} style={zoomBtnStyle} title="Alles anzeigen">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" />
            <path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
          </svg>
        </button>
      </div>

      {/* ── Tool bar (bottom-center, floating pill) ─────────────── */}
      <div style={{
        position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
        zIndex: 100, display: 'flex', alignItems: 'center', gap: 1,
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
        padding: '4px 6px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        {TOOLS.map(({ tool, label }) => {
          const active = activeTool === tool

          // Shape button gets special treatment (dropdown)
          if (tool === 'shape') {
            return (
              <div key={tool} style={{ position: 'relative' }}>
                <button
                  title={`${label} (${SHAPE_SUB_LABELS[shapeSubType]})`}
                  onClick={handleShapeClick}
                  onContextMenu={handleShapeContextMenu}
                  onPointerDown={handleShapePointerDown}
                  onPointerUp={handleShapePointerUp}
                  onPointerLeave={handleShapePointerUp}
                  style={{
                    width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: 'none', borderRadius: 8, cursor: 'pointer', position: 'relative',
                    background: active ? '#e0e7ff' : 'transparent',
                    color: active ? '#3b82f6' : '#555',
                    transition: 'background 0.1s, color 0.1s',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f3f4f6' }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent' }}
                >
                  {shapeSubIcons[shapeSubType]}
                  {/* Small dropdown triangle indicator */}
                  <svg width="6" height="6" viewBox="0 0 6 6" style={{ position: 'absolute', bottom: 3, right: 3 }}>
                    <polygon points="0,0 6,0 6,6" fill={active ? '#3b82f6' : '#999'} />
                  </svg>
                </button>

                {/* Shape sub-type dropdown */}
                {shapeDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute', bottom: 42, left: '50%', transform: 'translateX(-50%)',
                      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)', padding: 4,
                      display: 'flex', flexDirection: 'column', gap: 2, zIndex: 200,
                      minWidth: 120,
                    }}
                    onMouseLeave={() => setShapeDropdownOpen(false)}
                  >
                    {SHAPE_SUB_ORDER.map((sub) => (
                      <button
                        key={sub}
                        onClick={() => {
                          onShapeSubTypeChange(sub)
                          onToolChange('shape')
                          setShapeDropdownOpen(false)
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 10px', border: 'none', borderRadius: 6,
                          cursor: 'pointer', fontSize: 12,
                          background: shapeSubType === sub ? '#e0e7ff' : 'transparent',
                          color: shapeSubType === sub ? '#3b82f6' : '#333',
                        }}
                        onMouseEnter={(e) => { if (shapeSubType !== sub) e.currentTarget.style.background = '#f3f4f6' }}
                        onMouseLeave={(e) => { if (shapeSubType !== sub) e.currentTarget.style.background = 'transparent' }}
                      >
                        {shapeSubIcons[sub]}
                        <span>{SHAPE_SUB_LABELS[sub]}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          }

          return (
            <button
              key={tool}
              title={label}
              onClick={() => onToolChange(tool)}
              style={{
                width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', borderRadius: 8, cursor: 'pointer',
                background: active ? '#e0e7ff' : 'transparent',
                color: active ? '#3b82f6' : '#555',
                transition: 'background 0.1s, color 0.1s',
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f3f4f6' }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent' }}
            >
              {icons[tool]}
            </button>
          )
        })}

        {/* Divider */}
        <div style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 2px' }} />

        {/* Style panel toggle */}
        <div style={{ position: 'relative' }}>
          <button
            title="Stil"
            onClick={() => setStylePanelOpen((v) => !v)}
            style={{
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none', borderRadius: 8, cursor: 'pointer',
              background: stylePanelOpen ? '#e0e7ff' : 'transparent',
              color: stylePanelOpen ? '#3b82f6' : '#555',
              transition: 'background 0.1s, color 0.1s',
              position: 'relative',
            }}
            onMouseEnter={(e) => { if (!stylePanelOpen) e.currentTarget.style.background = '#f3f4f6' }}
            onMouseLeave={(e) => { if (!stylePanelOpen) e.currentTarget.style.background = 'transparent' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
            </svg>
            {/* Color indicator dot */}
            <div style={{
              position: 'absolute', bottom: 4, right: 4,
              width: 6, height: 6, borderRadius: '50%',
              background: currentStyle.color,
              border: '1px solid #fff',
            }} />
          </button>

          {/* Style panel popup */}
          {stylePanelOpen && (
            <StylePanel
              activeTool={activeTool}
              currentStyle={currentStyle}
              setCurrentStyle={setCurrentStyle}
              onClose={() => setStylePanelOpen(false)}
            />
          )}
        </div>
      </div>
    </>
  )
}

const zoomBtnStyle: React.CSSProperties = {
  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: 'none', borderRadius: 6, cursor: 'pointer',
  background: 'transparent', color: '#555',
}

// ── Style Panel Component ─────────────────────────────────────────────────────

function StylePanel({ activeTool, currentStyle, setCurrentStyle, onClose }: {
  activeTool: Tool
  currentStyle: BoardStyle
  setCurrentStyle: (patch: Partial<BoardStyle>) => void
  onClose: () => void
}) {
  const isDrawTool = ['draw', 'arrow', 'line', 'shape'].includes(activeTool)
  const isTextTool = ['text', 'sticky'].includes(activeTool)
  const isShapeTool = activeTool === 'shape'
  const isDashTool = ['line', 'shape', 'arrow'].includes(activeTool)

  const stylePanelLabelStyle: React.CSSProperties = {
    fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 4,
  }

  const radioStyle = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px', fontSize: 11, border: '1px solid #ddd', borderRadius: 4,
    cursor: 'pointer', background: active ? '#e0e7ff' : '#fff',
    color: active ? '#3b82f6' : '#555', fontFamily: 'inherit',
  })

  return (
    <div
      style={{
        position: 'absolute', bottom: 44, left: '50%', transform: 'translateX(-50%)',
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: 14,
        zIndex: 300, minWidth: 220,
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Color picker */}
      <div style={stylePanelLabelStyle}>Farbe</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {STYLE_COLORS.map((c) => (
          <button
            key={c}
            onClick={() => setCurrentStyle({ color: c })}
            style={{
              width: 22, height: 22, borderRadius: '50%', border: currentStyle.color === c ? '2px solid #3b82f6' : '2px solid #e5e7eb',
              background: c, cursor: 'pointer', padding: 0,
            }}
          />
        ))}
      </div>

      {/* Stroke width — for draw tools */}
      {isDrawTool && (
        <>
          <div style={stylePanelLabelStyle}>Strichstaerke</div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
            {STROKE_WIDTHS.map((sw) => (
              <button
                key={sw.value}
                onClick={() => setCurrentStyle({ strokeWidth: sw.value })}
                style={radioStyle(currentStyle.strokeWidth === sw.value)}
              >
                {sw.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Fill — only for shape tool */}
      {isShapeTool && (
        <>
          <div style={stylePanelLabelStyle}>Fuellung</div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
            {FILL_OPTIONS.map((fo) => (
              <button
                key={fo.value}
                onClick={() => setCurrentStyle({ fill: fo.value })}
                style={radioStyle(currentStyle.fill === fo.value)}
              >
                {fo.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Dash style — for line/rect/arrow */}
      {isDashTool && (
        <>
          <div style={stylePanelLabelStyle}>Linienstil</div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
            {DASH_OPTIONS.map((d) => (
              <button
                key={d.value}
                onClick={() => setCurrentStyle({ dash: d.value })}
                style={radioStyle(currentStyle.dash === d.value)}
              >
                {d.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Font size — for text/sticky */}
      {isTextTool && (
        <>
          <div style={stylePanelLabelStyle}>Schriftgroesse</div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
            {FONT_SIZES.map((fs) => (
              <button
                key={fs.value}
                onClick={() => setCurrentStyle({ fontSize: fs.value })}
                style={radioStyle(currentStyle.fontSize === fs.value)}
              >
                {fs.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Show generic color+stroke for non-specific tools */}
      {!isDrawTool && !isTextTool && (
        <>
          <div style={stylePanelLabelStyle}>Strichstaerke</div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
            {STROKE_WIDTHS.map((sw) => (
              <button
                key={sw.value}
                onClick={() => setCurrentStyle({ strokeWidth: sw.value })}
                style={radioStyle(currentStyle.strokeWidth === sw.value)}
              >
                {sw.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
